import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useSubscription } from "@/contexts/SubscriptionContext";
import { api } from "@/lib/api";
import { Loader2, ArrowRight, Calendar, MessageCircle, Award, Anchor, Camera, Code, Compass, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { SCENIC_IMG, FOUNDER_IMG } from "@/lib/toolRegistry";

export default function Consulting() {
  const { user } = useAuth();
  const { startCheckout } = useSubscription();
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ name: user?.name || "", email: user?.email || "", message: "" });
  const [sent, setSent] = useState(false);

  const book = async () => {
    if (!user) { nav("/register?next=/consulting"); return; }
    setBusy(true);
    try {
      const url = await startCheckout("consulting");
      window.location.href = url;
    } catch { toast.error("Could not start checkout"); setBusy(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/contact", { ...form, topic: "consulting" });
      setSent(true);
      toast.success("Got it - we'll reply within 24 hours.");
    } catch { toast.error("Could not send message"); }
  };

  return (
    <div data-testid="consulting-page" className="min-h-screen bg-[#F8FAFC]">
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-[#F8FAFC]/85 border-b border-[#E2E8F0]/60">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-10 h-14 sm:h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-md bg-[#0A2342] flex items-center justify-center">
              <span className="text-[#4FD1C5] font-heading font-bold text-xs sm:text-sm">D</span>
            </div>
            <span className="font-heading font-bold text-[13px] sm:text-[15px] text-[#0A2342]">Dive Media Strategist™</span>
          </Link>
          <Link to={user ? "/dashboard" : "/login"} className="dms-btn-ghost text-[13px] sm:text-[14px]">{user ? "Dashboard" : "Sign in"}</Link>
        </div>
      </header>

      <section className="relative overflow-hidden">
        <div className="absolute inset-0 -z-0">
          <img src={SCENIC_IMG} alt="" className="w-full h-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#F8FAFC] via-[#F8FAFC]/40 to-[#F8FAFC]" />
        </div>
        <div className="relative max-w-7xl mx-auto px-5 sm:px-6 lg:px-10 pt-12 sm:pt-20 pb-16 sm:pb-24 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="dms-badge mb-5"><Calendar className="w-3 h-3" /> 1:1 with Robin</div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold leading-tight text-[#0A2342]">
              Work directly with the creator of<br />
              <span className="text-[#1D4E89]">Dive Media Strategist.</span>
            </h1>
            <p className="mt-5 sm:mt-6 text-[16px] sm:text-[17px] text-[#475569] leading-relaxed max-w-2xl">
              A focused 45-minute discovery call. A complete review of where your dive business is leaking growth.
              A clear marketing roadmap. And one full month of Pro included, so we can build the system together.
            </p>
            <div className="mt-7 sm:mt-8 grid grid-cols-2 sm:grid-cols-4 gap-3 text-[13px] text-[#1F2937]">
              {[
                { icon: Award, t: "PADI Staff Instructor" },
                { icon: Anchor, t: "Freedive Instructor" },
                { icon: Sparkles, t: "Tech Diver" },
                { icon: Camera, t: "Content creator" },
                { icon: Code, t: "AI systems builder" },
                { icon: Compass, t: "3,000+ dives" },
              ].map(({ icon: Icon, t }) => (
                <div key={t} className="flex items-center gap-2"><Icon className="w-4 h-4 text-[#4FD1C5]" /> <span>{t}</span></div>
              ))}
            </div>
            <div className="mt-8 sm:mt-10 dms-card p-6 sm:p-7 max-w-xl">
              <div className="flex items-baseline justify-between">
                <div>
                  <div className="dms-label mb-1">One-time</div>
                  <div className="font-heading font-bold text-4xl sm:text-5xl text-[#0A2342]">€150</div>
                </div>
                <div className="dms-badge">1 month Pro included</div>
              </div>
              <ul className="mt-5 space-y-2 text-[14px] text-[#1F2937]">
                <li>• 45-minute discovery call</li>
                <li>• Business marketing audit</li>
                <li>• Growth opportunities assessment</li>
                <li>• 1 month Pro subscription</li>
                <li>• €150 credit toward larger consulting or 3 months Pro</li>
              </ul>
              <div className="mt-6 flex flex-col sm:flex-row gap-3">
                <button data-testid="consulting-book" onClick={book} disabled={busy} className="dms-btn-accent flex-1 justify-center">
                  {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Opening checkout…</> : <>Book consultation <ArrowRight className="w-4 h-4" /></>}
                </button>
                <a href="#chat" data-testid="consulting-chat-cta" className="dms-btn-ghost flex-1 justify-center"><MessageCircle className="w-4 h-4" /> Prefer to chat first?</a>
              </div>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="relative">
              <div className="absolute -inset-6 rounded-[28px] bg-[#1D4E89]/10 blur-2xl" />
              <div className="relative dms-card p-1.5 overflow-hidden">
                <img src={FOUNDER_IMG} alt="Robin underwater" className="w-full h-[320px] sm:h-[460px] object-cover rounded-[10px]" />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-10 py-16 sm:py-20 border-t border-[#E2E8F0]">
        <div className="max-w-3xl">
          <div className="dms-label mb-3">If we work together long-term</div>
          <h2 className="text-3xl sm:text-4xl font-heading font-bold text-[#0A2342]">Larger consulting projects can include…</h2>
        </div>
        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
          {[
            "Marketing strategy", "Content systems", "Lead generation", "Funnel design",
            "Email marketing", "SEO strategy", "Meta advertising", "Google advertising",
            "Custom AI business agents", "Long-term content management", "Long-term SEO campaigns", "Brand positioning",
          ].map((s) => (
            <div key={s} className="dms-card p-5">
              <div className="font-heading font-bold text-[15px] text-[#0A2342]">{s}</div>
            </div>
          ))}
        </div>
        <p className="mt-8 text-[#475569] text-[14.5px] max-w-3xl leading-relaxed">
          We can also design and set up a <strong className="text-[#0A2342]">custom AI business agent</strong> tailored to your business - your audience, your offers, your workflow.
          We use the €150 discovery call to scope this. The €150 fee is credited toward any larger engagement or 3 months of Pro, your choice.
        </p>
      </section>

      <section id="chat" className="bg-white border-t border-[#E2E8F0]">
        <div className="max-w-3xl mx-auto px-5 sm:px-6 lg:px-10 py-16 sm:py-20">
          <div className="dms-label mb-3">Prefer to chat first?</div>
          <h2 className="text-3xl sm:text-4xl font-heading font-bold text-[#0A2342]">Send a short message.</h2>
          <p className="mt-3 text-[#475569] text-[14.5px]">We&apos;ll reply within 24 hours. No pressure.</p>
          {sent ? (
            <div data-testid="contact-sent" className="mt-8 dms-card p-6 bg-[#F8FAFC]">
              <div className="font-heading font-bold text-[#0A2342]">Message received.</div>
              <p className="mt-2 text-[#475569] text-[14.5px]">We&apos;ve sent a confirmation to {form.email}. Talk soon.</p>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-8 dms-card p-6 space-y-4">
              <div>
                <label className="dms-label block mb-2">Name</label>
                <input data-testid="contact-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="dms-input" />
              </div>
              <div>
                <label className="dms-label block mb-2">Email</label>
                <input data-testid="contact-email" required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="dms-input" />
              </div>
              <div>
                <label className="dms-label block mb-2">Message</label>
                <textarea data-testid="contact-message" required rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="dms-textarea" placeholder="What's your business and what would you like to discuss?" />
              </div>
              <div className="flex justify-end">
                <button data-testid="contact-submit" type="submit" className="dms-btn-primary">Send <ArrowRight className="w-4 h-4" /></button>
              </div>
            </form>
          )}
        </div>
      </section>

      <footer className="border-t border-[#E2E8F0] py-8 text-center text-[11px] tracking-[0.18em] text-[#64748B] uppercase">
        © Dive Media Strategist™ · Engineered for divers who create
      </footer>
    </div>
  );
}
